import CloseIcon from '@mui/icons-material/Close';
import { Button, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';

import { useAuthStore } from '@/features/auth/stores/authStore';
import { Rol } from '@/types/enums';

import { BulkActionType } from '../constants';
import type { Protocolo } from '../types';

interface ProtocolosBulkActionsBarProps {
  selected: Protocolo[];
  onAction: (actionType: BulkActionType) => void;
  onCancel: () => void;
}

export function ProtocolosBulkActionsBar({
  selected,
  onAction,
  onCancel,
}: ProtocolosBulkActionsBarProps): JSX.Element | null {
  const currentUser = useAuthStore((s) => s.currentUser);
  const isAdmin = currentUser?.rol === Rol.ADMINISTRADOR;
  const canCerrar =
    currentUser?.rol === Rol.ADMINISTRADOR || currentUser?.rol === Rol.TECNICO_LABORATORIO;

  if (selected.length === 0 || (!isAdmin && !canCerrar)) return null;

  const allFacturado = selected.every((p) => p.facturado);
  const noneFacturado = selected.every((p) => !p.facturado);
  const mixedFacturado = !allFacturado && !noneFacturado;

  const allCobrado = selected.every((p) => p.cobrado);
  const noneCobrado = selected.every((p) => !p.cobrado);
  const mixedCobrado = !allCobrado && !noneCobrado;

  const allAbiertos = selected.every((p) => !p.informe_final_emitido);
  const allCerrados = selected.every((p) => p.informe_final_emitido);
  const mixedCierre = !allAbiertos && !allCerrados;

  function tooltipEstado(mixed: boolean, target: boolean, enabled: boolean, campo: string): string {
    if (enabled) return '';
    if (mixed) return `Algunos protocolos ya están ${campo}`;
    return target ? `Todos los protocolos ya están ${campo}` : `Ningún protocolo está ${campo}`;
  }

  const btnFacturarEnabled = noneFacturado;
  const btnDesfacturarEnabled = allFacturado;
  const btnCobrarEnabled = noneCobrado;
  const btnDescobrarEnabled = allCobrado;
  const btnCerrarEnabled = allAbiertos;

  const tooltipFacturar = tooltipEstado(mixedFacturado, true, btnFacturarEnabled, 'facturados');
  const tooltipDesfacturar = tooltipEstado(mixedFacturado, false, btnDesfacturarEnabled, 'facturado');
  const tooltipCobrar = tooltipEstado(mixedCobrado, true, btnCobrarEnabled, 'cobrados');
  const tooltipDescobrar = tooltipEstado(mixedCobrado, false, btnDescobrarEnabled, 'cobrado');
  const tooltipCerrar = mixedCierre
    ? 'Algunos protocolos ya están cerrados'
    : allCerrados
      ? 'Todos los protocolos ya están cerrados'
      : '';

  return (
    <Paper
      elevation={2}
      sx={{
        px: 2,
        py: 1.25,
        mb: 1.5,
        bgcolor: 'primary.veryLight',
        border: '1px solid',
        borderColor: 'primary.light',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
          {selected.length} {selected.length === 1 ? 'protocolo seleccionado' : 'protocolos seleccionados'}
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ flexGrow: 1 }}
        >
          {isAdmin && (
            <>
              <Tooltip title={tooltipFacturar}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!btnFacturarEnabled}
                    onClick={() => onAction(BulkActionType.FACTURADO)}
                  >
                    Marcar como facturado
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={tooltipDesfacturar}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!btnDesfacturarEnabled}
                    onClick={() => onAction(BulkActionType.NO_FACTURADO)}
                  >
                    Marcar como no facturado
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={tooltipCobrar}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!btnCobrarEnabled}
                    onClick={() => onAction(BulkActionType.COBRADO)}
                  >
                    Marcar como cobrado
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={tooltipDescobrar}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!btnDescobrarEnabled}
                    onClick={() => onAction(BulkActionType.NO_COBRADO)}
                  >
                    Marcar como no cobrado
                  </Button>
                </span>
              </Tooltip>
            </>
          )}

          {canCerrar && (
            <Tooltip title={tooltipCerrar}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!btnCerrarEnabled}
                  onClick={() => onAction(BulkActionType.CERRAR)}
                >
                  Cerrar protocolo
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>

        <Tooltip title="Cancelar selección">
          <IconButton
            size="small"
            onClick={onCancel}
            sx={{ flexShrink: 0, alignSelf: { xs: 'flex-end', sm: 'center' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  );
}
